/* eslint-disable @typescript-eslint/no-this-alias */
import {ComponentInterface, getMode} from '@stencil/core';

export type PropMapping = {
  // Defining a type alias for an object with low and medium properties, each of which can be any value
  low: any;
  medium: any;
};
/**
 * Checks if a given value is allowed for a property based on the allowed types for a specific density.
 * @param property - The name of the property.
 * @param value - The value of the property.
 * @param lowDensityType - The allowed types for low density components.
 * @param mediumDensityType - The allowed types for medium density components.
 * @param density - The current density of the component.
 * @returns True if the given value is allowed, false otherwise.
 */
function allowedTypesChecker(
  property: string,
  value: string,
  lowDensityType: any,
  mediumDensityType: any,
  density: 'low' | 'medium'
) {
  const type = density === 'low' ? lowDensityType : mediumDensityType;
  const allowedValues = Object.values(type) as string[];
  const allowed = allowedValues.includes(value);
  if (!allowed) {
    console.error(
      `${value} is not a valid value for ${property}. Allowed values are ${allowedValues} for ${density} density components`
    );
  }
  return allowed;
}

/**
 * A decorator that enforces allowed types for a property based on the provided propMappings and defaults.
 * @param propMappings - An object that maps property names to an object containing the allowed types for low and medium density components.
 * @param defaults - An object that maps property names to default values.
 * @returns A decorator function.
 */
export function AllowedTypes(propMappings: {[key: string]: PropMapping}, defaults: {[key: string]: any}) {
  return (_target: ComponentInterface, _propertyKey: string, descriptor: PropertyDescriptor) => {
    const originalMethod = descriptor.value;
    descriptor.value = function (...args: any[]) {
      const componentInstance = this;
      const props = Object.keys(propMappings) as (keyof PropertyDescriptor)[]; // get all the keys of the propMappings object
      const mode = getMode(componentInstance) as 'low' | 'medium';
      // Loop through each property and check if the value is allowed based on density
      for (const prop of props) {
        const propMapping = propMappings[prop]; // get the allowed types for the current property
        const propValue = componentInstance[prop]; // get the current value of the property
        const isAllowed = allowedTypesChecker(prop, propValue, propMapping.low, propMapping.medium, mode);
        // If the value is not allowed, set it to the default value
        if (!isAllowed) {
          componentInstance[prop] = defaults[prop][mode];
        }
      }
      originalMethod.apply(componentInstance, args); // call the original method
    };
  };
}
